import React from 'react';
import type {QueryFilterAdapterModel, QueryFilterAdapterProps, QueryFilterProps} from '@igp/shared/components/filter';
import {withQueryFilter} from '@igp/shared/components/filter';
import type {AgentFilterSharedProps} from '@igp/shared/features/agent-profile';
import type {CountryFilterSharedProps} from '@igp/shared/features/country';
import {getMirrorObject} from '@igp/shared/utils';
import type {EntityType} from '@models';

import type {
    PlayerLevelReportV2FilterName,
    PlayerLevelReportV2FilterViewModel,
    PlayerLevelReportV2FilterViewModelTextKeys,
    PlayerLevelReportV2QueryFilterKeys,
    PlayerLevelReportV2QueryTextFilterKeys,
} from '../types';

import {PlayerLevelReportV2Filters} from './Filters';

type PlayerLevelReportV2FiltersAdapterProps = QueryFilterAdapterProps<
    PlayerLevelReportV2QueryFilterKeys,
    PlayerLevelReportV2FilterName,
    PlayerLevelReportV2QueryTextFilterKeys
> &
    CountryFilterSharedProps<EntityType> &
    AgentFilterSharedProps<EntityType>;

function PlayerLevelReportV2FiltersAdapter({model, onChange, ...props}: PlayerLevelReportV2FiltersAdapterProps) {
    const viewModelTextKeyToQueryTextKey: Record<PlayerLevelReportV2FilterViewModelTextKeys, PlayerLevelReportV2QueryTextFilterKeys> = {
        uid: 'uid',
        uid_username: 'uid_un',
        username: 'username',
    };
    const queryTextKeyToViewModelTextKey: Record<PlayerLevelReportV2QueryTextFilterKeys, PlayerLevelReportV2FilterViewModelTextKeys> =
        getMirrorObject(viewModelTextKeyToQueryTextKey);

    const filterViewModel: PlayerLevelReportV2FilterViewModel = {
        text: model?.text
            ? {
                  text: model.text.text,
                  option: queryTextKeyToViewModelTextKey[model?.text?.option],
              }
            : null,
        agent: {
            agentId: model?.referrer_id as string,
            isDownstream: model?.is_downstream as boolean,
        },
        countries: model?.country as string[],
        startedAt: {
            from: model?.started_at_from as number,
            to: model?.started_at_to as number,
        },
    };

    function handleChange(model: PlayerLevelReportV2FilterViewModel) {
        const result: QueryFilterAdapterModel<PlayerLevelReportV2QueryFilterKeys, PlayerLevelReportV2QueryTextFilterKeys> = {
            text: model?.text
                ? {
                      text: model.text.text,
                      option: viewModelTextKeyToQueryTextKey[model.text.option],
                  }
                : null,
            country: model?.countries,
            started_at_from: model?.startedAt?.from,
            started_at_to: model?.startedAt?.to,
            referrer_id: model?.agent?.agentId,
            is_downstream: model?.agent?.isDownstream,
        };
        onChange(result);
    }

    return <PlayerLevelReportV2Filters model={filterViewModel} onChange={handleChange} {...props} />;
}

export const PlayerLevelReportV2QueryFilters: React.ComponentType<
    QueryFilterProps<PlayerLevelReportV2QueryFilterKeys, PlayerLevelReportV2FilterName> &
        CountryFilterSharedProps<string> &
        AgentFilterSharedProps<string>
> = withQueryFilter<
    PlayerLevelReportV2QueryFilterKeys,
    PlayerLevelReportV2FilterName,
    PlayerLevelReportV2QueryTextFilterKeys,
    CountryFilterSharedProps<string> & AgentFilterSharedProps<string>
>(PlayerLevelReportV2FiltersAdapter, {
    uid_username: ['uid_un', 'uid', 'username'],
});
